// src/components/TwoFactorSetup.jsx
// Two-Factor Authentication Setup Component with QR Code

import React, { useState, useEffect } from 'react';
import {
  Shield,
  Smartphone,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
  Key
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const TwoFactorSetup = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1); // 1: intro, 2: scan QR, 3: verify, 4: backup codes
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Setup data
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);

  // Start the setup process
  const startSetup = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/mfa/setup/start`, {
        method: 'POST',
        credentials: 'include', // Use httpOnly cookie
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to start 2FA setup');
      }

      setSetupData(data.setup);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify the code and complete setup
  const verifyAndComplete = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/mfa/setup/verify`, {
        method: 'POST',
        credentials: 'include', // Use httpOnly cookie
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: verificationCode })
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      // Save backup codes to show
      setBackupCodes(data.backupCodes);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Copy secret to clipboard
  const copySecret = async () => {
    if (setupData?.secret) {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Copy backup codes to clipboard
  const copyBackupCodes = async () => {
    const codesText = backupCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setBackupCodesCopied(true);
    setTimeout(() => setBackupCodesCopied(false), 2000);
  };

  // Handle code input - only numbers
  const handleCodeInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
          <Shield className="text-blue-600" size={24} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#172A3A]">
            Two-Factor Authentication
          </h2>
          <p className="text-sm text-gray-600">
            Add an extra layer of security to your account
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Step 1: Introduction */}
      {step === 1 && (
        <div>
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="font-semibold text-amber-800 mb-2">What is 2FA?</h3>
            <p className="text-sm text-amber-700">
              Two-factor authentication adds an extra security step when logging in.
              After entering your password, you'll also need a code from your phone's
              authenticator app. This protects your account even if your password is compromised.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="font-semibold text-[#172A3A] mb-3">You'll need:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-gray-700">
                <Smartphone size={18} className="text-blue-600" />
                <span>A smartphone with an authenticator app installed</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <Key size={18} className="text-blue-600" />
                <span>Google Authenticator, Authy, or Microsoft Authenticator</span>
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={startSetup}
              disabled={loading}
              className="flex-1 bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 px-4 rounded-lg transition disabled:opacity-50"
            >
              {loading ? 'Starting...' : 'Get Started'}
            </button>
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Scan QR Code */}
      {step === 2 && setupData && (
        <div>
          <div className="mb-4">
            <h3 className="font-semibold text-[#172A3A] mb-2">
              Step 1: Scan QR Code
            </h3>
            <p className="text-sm text-gray-600">
              Open your authenticator app and scan this QR code
            </p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
              <img
                src={setupData.qrCode}
                alt="2FA QR Code"
                className="w-48 h-48"
              />
            </div>
          </div>

          {/* Manual Entry Option */}
          <div className="mb-6">
            <button
              onClick={() => setShowSecret(!showSecret)}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              {showSecret ? 'Hide' : 'Show'} manual entry key
            </button>

            {showSecret && (
              <div className="mt-2 p-3 bg-gray-100 rounded-lg">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-800 break-all">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={copySecret}
                    className="ml-2 p-1 hover:bg-gray-200 rounded"
                    title="Copy to clipboard"
                  >
                    {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter this key manually if you can't scan the QR code
                </p>
              </div>
            )}
          </div>

          <button
            onClick={() => setStep(3)}
            className="w-full bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 px-4 rounded-lg transition"
          >
            I've Scanned the Code
          </button>
        </div>
      )}

      {/* Step 3: Verify Code */}
      {step === 3 && (
        <div>
          <div className="mb-4">
            <h3 className="font-semibold text-[#172A3A] mb-2">
              Step 2: Enter Verification Code
            </h3>
            <p className="text-sm text-gray-600">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <div className="mb-6">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={verificationCode}
              onChange={handleCodeInput}
              placeholder="000000"
              className="w-full text-center text-3xl font-mono tracking-widest py-4 border-2 border-gray-300 rounded-lg focus:border-[#FFB800] focus:ring-2 focus:ring-[#FFB800]/20 outline-none"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              The code changes every 30 seconds
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Back
            </button>
            <button
              onClick={verifyAndComplete}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1 bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 px-4 rounded-lg transition disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Backup Codes */}
      {step === 4 && (
        <div>
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700 font-semibold mb-1">
              <Check size={18} />
              2FA Successfully Enabled!
            </div>
            <p className="text-sm text-green-600">
              Your account is now protected with two-factor authentication.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="font-semibold text-[#172A3A] mb-2 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Save Your Backup Codes
            </h3>
            <p className="text-sm text-gray-600">
              If you lose access to your authenticator app, you can use these codes to sign in.
              <strong className="text-red-600"> These will only be shown once!</strong>
            </p>
          </div>

          {/* Backup Codes Grid */}
          <div className="mb-4 p-4 bg-gray-100 rounded-lg">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="font-mono text-sm bg-white px-3 py-2 rounded border text-center"
                >
                  {code}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={copyBackupCodes}
            className="w-full mb-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2"
          >
            {backupCodesCopied ? (
              <>
                <Check size={16} className="text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy size={16} />
                Copy All Codes
              </>
            )}
          </button>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <p className="text-sm text-amber-700">
              <strong>Important:</strong> Store these codes in a safe place (password manager,
              secure note, or printed copy). Each code can only be used once.
            </p>
          </div>

          <button
            onClick={() => onComplete && onComplete()}
            className="w-full bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 px-4 rounded-lg transition"
          >
            I've Saved My Backup Codes
          </button>
        </div>
      )}

      {/* Step indicator */}
      <div className="mt-6 flex justify-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full transition ${
              s <= step ? 'bg-[#FFB800]' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default TwoFactorSetup;
